import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import type { RealtimeQuote } from '../types';
import { formatMoney, getPriceColor, formatPct, formatNumber } from '../utils/format';
import { DEFAULT_BUY_QUANTITY } from '../utils/constants';

interface BuyConfirmDialogProps {
  open: boolean;
  quote: RealtimeQuote | null;
  onClose: () => void;
  onConfirm: (code: string, price: number, quantity: number) => Promise<void>;
}

/**
 * 买入确认弹窗
 * 输入数量、显示当前价、计算成交金额
 */
export default function BuyConfirmDialog({ open, quote, onClose, onConfirm }: BuyConfirmDialogProps) {
  const [quantity, setQuantity] = useState(DEFAULT_BUY_QUANTITY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setQuantity(DEFAULT_BUY_QUANTITY);
      setError('');
    }
  }, [open]);

  if (!quote) return null;

  const amount = quote.price * quantity;

  const handleConfirm = async () => {
    if (quantity <= 0) {
      setError('买入数量必须大于0');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await onConfirm(quote.code, quote.price, quantity);
      onClose();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>确认买入</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {/* 股票信息 */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6">{quote.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {quote.code}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="h5" sx={{ color: getPriceColor(quote.changePct), fontWeight: 700 }}>
                {formatNumber(quote.price)}
              </Typography>
              <Typography variant="body2" sx={{ color: getPriceColor(quote.changePct) }}>
                {formatPct(quote.changePct)}
              </Typography>
            </Box>
          </Box>

          {/* 数量输入 */}
          <TextField
            label="买入数量（股）"
            type="number"
            value={quantity}
            onChange={(e) => setQuantity(parseInt(e.target.value) || 0)}
            inputProps={{ min: 100, step: 100 }}
            helperText="A股买入数量需为100的整数倍（1手=100股）"
          />

          {/* 成交金额 */}
          <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">买入价</Typography>
              <Typography variant="body2">¥{formatNumber(quote.price)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">数量</Typography>
              <Typography variant="body2">{quantity} 股</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>成交金额</Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, color: 'error.main' }}>
                {formatMoney(amount)}
              </Typography>
            </Box>
          </Box>

          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>取消</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleConfirm}
          disabled={loading}
        >
          {loading ? '买入中...' : '确认买入'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
